<template>
  <v-data-table
    :headers="headers"
    :loading="loading"
    :server-items-length="serverItemsLength"
    :items="items"
    :options.sync="computedOptions"
    :footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
    :items-per-page="itemsPerPage"
  >
    <template v-slot:item.image_url="{ item }">
      <div class="img-container d-flex align-center justify-center">
        <img v-if="item.image_url && item.image_url.length" :src="item.image_url" alt="" width="100%" />
        <mf-icon v-else>image_not_supported</mf-icon>
      </div>
    </template>

    <template v-slot:item.sku="{ item, header }">
      <div :style="`width: ${header.width}`">
        <span v-if="item.products !== null">
          <v-tooltip max-width="250" right>
            <template v-slot:activator="{ on }">
              <span v-on="on">{{ item.products.length === 1 ? '1 código' : `${item.products.length} códigos` }}</span>
            </template>
            <span v-for="(product, index) in item.products" :key="index" style="display: block">
              {{ product.sku }}
            </span>
          </v-tooltip>
        </span>
        <a v-else-if="item.sku" target="_blank" class="link" :href="link(item.sku)">{{ item.sku }}</a>
        <span v-else> - </span>
      </div>
    </template>

    <template v-slot:item.name="{ item, header }">
      <div :style="`width: ${header.width}`">
        <v-tooltip top>
          <template #activator="{ on }">
            <span class="texto-corrido" v-on="on">
              {{ putEllipsis(item.name, 25) }}
            </span>
          </template>
          <span>{{ item.name }}</span>
        </v-tooltip>
      </div>
    </template>

    <template v-slot:item.units="{ item, header }">
      <div :style="`width: ${header.width}`">
        <v-tooltip v-if="item.app_units.length > 1" right>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" style="cursor: auto" v-on="on">
              {{ getUnits(item) }}
            </span>
          </template>
          <span v-for="(unit, index) in item.app_units" :key="`${unit.name}-${index}`" style="display: block">
            {{ unit.name }}
          </span>
        </v-tooltip>
        <span v-else-if="item.app_units.length === 1">
          {{ item.app_units[0].name }}
        </span>
        <span v-else> - </span>
      </div>
    </template>

    <template v-slot:item.activatable="{ item, header }">
      <div :style="`width: ${header.width}`">
        {{ item.activatable ? 'Ativável' : 'Livre' }}
      </div>
    </template>

    <template v-slot:item.personalized="{ item, header }">
      <div :style="`width: ${header.width}`">
        {{ getProductTypeLabel(item.personalized) }}
      </div>
    </template>

    <template v-slot:item.type="{ item, header }">
      <div :style="`width: ${header.width}`">
        {{ getProductFormatTypeLabel(item.type) }}
      </div>
    </template>

    <template v-slot:item.dates="{ item, header }">
      <div :style="`width: ${header.width}`" class="d-flex align-center">
        <date-time-status-colors :start-date="item.start_date" :end-date="item.end_date" />
        <div>
          {{ validityDates(item) }}
        </div>
      </div>
    </template>

    <template v-slot:item.cashback_absolute="{ item, header }">
      <div :style="`width: ${header.width}`">
        <span v-if="item.cashback_percentage">{{ item.cashback_percentage | localeString(0) | percent }}</span>
        <span v-else-if="item.cashback_absolute">{{ item.cashback_absolute | localeString(2) | currency }}</span>
        <span v-else>-</span>
      </div>
    </template>

    <template v-slot:item.additional_points_rate="{ item, header }">
      <div :style="`width: ${header.width}`">
        <span v-if="item.additional_points_rate">{{ item.additional_points_rate | localeString(0) }}%</span>
        <span v-else>-</span>
      </div>
    </template>

    <template v-slot:item.discount="{ item, header }">
      <div :style="`width: ${header.width}`">
        <span>{{ item.discount_percentage ? formatPercent(item.discount_percentage) : '-' }}</span>
      </div>
    </template>

    <template v-if="allowsEditingOfferUnits" v-slot:item.action="{ item }">
      <div class="text-end">
        <v-progress-circular v-if="item.loading" indeterminate color="primary" size="15" />
        <v-menu v-else bottom origin="center center" transition="scale-transition">
          <template v-slot:activator="{ on }">
            <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
          </template>

          <v-list>
            <v-list-item @click="$emit('open-edit-shop-dialog', item, isFranchise)">
              <v-list-item-title>Editar lojas em exibição</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="item.products !== null && !isFranchisee" @click="$emit('edit-group', item)">
              <v-list-item-title>Editar grupo</v-list-item-title>
            </v-list-item>
            <v-list-item v-else-if="showEditButton(item)" @click="$emit('open-edit-dialog', item)">
              <v-list-item-title>Editar produto</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import { getProductTypeLabel, getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { DateTime } from 'luxon'
import { localeString, putEllipsis, floatToPercentString } from '@/config/helpers.js'
export default {
  name: 'OffersProductsTable',
  components: {
    DateTimeStatusColors: () => import('./DateTimeStatusColors.vue'),
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    serverItemsLength: {
      type: Number,
      default: 0,
    },
    isFranchise: {
      type: Boolean,
      default: false,
    },
    myOffersTable: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
  },
  computed: {
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    allowsEditingOfferUnits() {
      if (!this.isFranchisee) {
        return true // All non-franchisee users can edit offer units
      }
      if (this.$permissions.havePagePermission('mobile/ofertas/produtos/edicao-lojas-franquia')) {
        return true // If is franchisee and have permissions for editing units on offers, return true
      }
      return false
    },
    computedHeaders() {
      if (this.allowsEditingOfferUnits) {
        return this.headers.filter((header) => header.value !== 'action')
      }
      return this.headers
    },
    computedOptions: {
      get() {
        return this.options
      },
      set(value) {
        this.$emit('update:options', value)
      },
    },
  },
  methods: {
    getProductTypeLabel,
    getProductFormatTypeLabel,
    putEllipsis,
    link(sku) {
      return `#/produtos/detalhes-produto/${sku}`
    },
    formatPercent(value) {
      return floatToPercentString(value)
    },
    validityDates(item) {
      return `${DateTime.fromISO(item.start_date).toFormat('dd/MM/yyyy')}-${DateTime.fromISO(item.end_date).toFormat('dd/MM/yyyy')}`
    },
    getItemDiscount(item) {
      const { type, price, discounted_price, take, pay, target_products, percentage_discount } = item

      const discounts = {
        general: !this.isFranchise
          ? `R$ ${localeString(discounted_price, 2)} / R$ ${localeString(price, 2)}`
          : price
          ? `R$ ${localeString(discounted_price, 2)} / R$ ${localeString(price, 2)}`
          : '-',
        percentage: `${percentage_discount ? percentage_discount + '%' : '-'}`,
        takepay: !take || !pay ? '-' : `${take} / ${pay}`,
        combo: `${target_products?.percentage_discount ? target_products.percentage_discount + '%' : '-'}`,
        cashback_percentage: '-',
        cashback_absolute: '-',
        points: '-',
      }

      return discounts[type]
    },
    getUnits(item) {
      if (!item.app_units || !item.app_units.length) return '-'
      else if (item.app_units.length >= 2) return `${item.app_units.length} lojas`
      return item.app_units[0].name
    },
    showEditButton(item) {
      if (this.isFranchisee) return this.myOffersTable && item.isEditable
      else return item.isEditable
    },
  },
}
</script>

<style lang="scss" scoped>
.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
</style>
